// The Stock list and the source-group drawer (CONTROL V4 sd-module Stock,
// stRows / stFacts / stHist; handoff §7n.5). Every figure is the API's ledger
// (services/stock-ledger.js); nothing here works out a stock number, it only
// filters, totals and words what the API returned.

import { chipLabel } from '../../lib/tagLabel'
import { fmtDay, fmtStamp, plural } from './model'

export interface StockRow {
  id: string
  picking_job_id?: string
  harvest_date: string | null
  variety: string
  box_type: string
  capacity_per_pallet: number
  received_boxes: number
  job_name?: string | null
  site_name?: string | null
  contractor_name?: string | null
  lid_name?: string | null
  group_name?: string | null
  block_name?: string | null
  boxes_on_hand: number
  boxes_held: number
  boxes_reserved: number
  boxes_available: number
  boxes_palletised?: number
  boxes_to_palletise?: number
  boxes_confirmed_on_pallets?: number
  boxes_shortfall?: number
  hold_active?: boolean
  // The notes on this group's live pallets (186).
  pallet_notes?: PalletNote[]
}

export interface PalletNote {
  id: string
  pallet_id: string
  text: string
  by: string | null
  at: string | null
}

export interface Ledger {
  received: number
  departed: number
  written_off: number
  // Stock takes' net change (187): + found, − missing.
  adjusted?: number
  on_hand: number
  source_held: number
  held: number
  reserved: number
  available: number
  palletised: number
  confirmed_on_pallets: number
  to_palletise: number
  shortfall: number
  hold_active: boolean
}

export interface StockMovement {
  id: string
  movement_type: string
  quantity: number
  reason: string | null
  reference_id: string | null
  created_at: string
  created_by: string | null
}

export interface StockAllocation {
  id: string
  order_line_id: string
  order_id: string
  order_reference: string
  customer_name: string | null
  approval_status: string
  approval_label: string
  allocated_boxes: number
  released_boxes: number
  reserved: number
  provisional: boolean
}

export interface StockPallet {
  id: string
  pallet_id: string
  planned_boxes: number
  confirmed_boxes: number | null
  preparation_state: 'planned' | 'confirmed' | 'discrepancy'
  hold_state: string | null
  dispatch_id: string | null
  cancelled: boolean
  departed: boolean
  note?: string | null
  order_id: string | null
  order_reference: string | null
}

export interface StockReceipt {
  id: string
  at: string
  boxes: number
  by: string | null
  how: string | null
  cycle_id: string
  item_code: string | null
}

// Receipts outside Stock & Dispatch coverage (GET
// /api/stock-receipts/coverage-gap; Kerrin, 2026-10-01; migrations 115, 116):
// received while the workspace was unlicensed, received while licensed but
// read while it was not, or received when its licence is not on record. None
// is in stock, and restoring the licence did not add them.
export interface CoverageGap {
  licensed_since: string | null
  receipts: number
  first_received_at: string | null
  last_received_at: string | null
  by_reason?: Record<string, number>
  // What the list cannot vouch for; null is "since the workspace was created".
  licence_known_from?: string | null
  gaps_recorded_from?: string | null
}

// The coverage reading beside the stock. A read that failed, or a response
// that is not a reading, is 'failed': never "no receipts outside coverage".
export type Coverage =
  | { state: 'checking' }
  | { state: 'failed'; retrying?: boolean }
  | { state: 'checked'; gap: CoverageGap }

export function coverageFrom(g: unknown): Coverage {
  const x = g as CoverageGap | null
  return x && typeof x === 'object' && !Array.isArray(x) && typeof x.receipts === 'number' && x.receipts >= 0
    ? { state: 'checked', gap: x }
    : { state: 'failed' }
}

// The gap as the page shows it, or null when there is none (or the response
// is not one: a failed read never invents a gap).
export function coverageGap(g: unknown): CoverageGap | null {
  const x = g as CoverageGap | null
  return x && typeof x === 'object' && typeof x.receipts === 'number' && x.receipts > 0 ? x : null
}

export const coverageGapTitle = (g: CoverageGap) =>
  plural(g.receipts, 'receipt') + (g.receipts === 1 ? ' is' : ' are') + ' not in stock'

export const COVERAGE_FAILED_TITLE = 'Receipt coverage could not be checked'
export const COVERAGE_FAILED_LINE =
  'The stock below is shown as recorded. Whether any receipts were kept out of it could not be read, so this is not a sign that there are none.'

const REASON_LINES: [string, string][] = [
  ['not_licensed', 'received and read while this workspace was not licensed for Stock & Dispatch'],
  ['received_before_licence', 'received while it was not licensed, and read after the licence was restored'],
  // Recorded only before 1 Oct 2026 (LICHIST-1): such a receipt is now stock.
  ['processed_while_unlicensed', 'received while it was licensed and read while the licence was removed, under the rule before 1 Oct 2026. Receipts like this now become stock, but those recorded earlier are not added automatically'],
  ['licence_unknown', "received when this workspace's licence is not on record"],
]

export function coverageGapLines(g: CoverageGap): string[] {
  const when =
    g.first_received_at && g.last_received_at && fmtStamp(g.first_received_at) !== fmtStamp(g.last_received_at)
      ? 'between ' + fmtStamp(g.first_received_at) + ' and ' + fmtStamp(g.last_received_at)
      : 'at ' + fmtStamp(g.first_received_at || g.last_received_at)
  const reasons = g.by_reason
    ? REASON_LINES.filter(([k]) => (g.by_reason?.[k] || 0) > 0).map(([k, text]) => {
        const n = g.by_reason![k]
        return plural(n, 'receipt') + (n === 1 ? ' was ' : ' were ') + text + '.'
      })
    : null
  return [
    ...(reasons
      ? ['Received ' + when + '.', ...reasons]
      : ['Received ' + when + ', while this workspace was not licensed for Stock & Dispatch' +
          (g.licensed_since ? ' or before its current licence began (' + fmtStamp(g.licensed_since) + ')' : '') + '.']),
    ...(g.receipts === 1
      ? [
          'Restoring the licence did not add it, and it is not added automatically. The figures below leave it out.',
          'Bringing it into stock needs a separate, audited reconciliation, which is not available yet.',
        ]
      : [
          'Restoring the licence did not add them, and they are not added automatically. The figures below leave them out.',
          'Bringing them into stock needs a separate, audited reconciliation, which is not available yet.',
        ]),
    ...coverageLimitLines(g),
  ]
}

// What "no receipts outside coverage" cannot vouch for: receipts read before
// gaps were recorded (115), and receipts from before the licence history
// (116). Empty when both go back to the workspace's creation.
export function coverageLimitLines(g: CoverageGap): string[] {
  return [
    ...(g.gaps_recorded_from
      ? ['Receipts read before ' + fmtStamp(g.gaps_recorded_from) + ' were not checked for coverage, so none from before then are listed here.']
      : []),
    ...(g.licence_known_from
      ? ["This workspace's licence history is on record from " + fmtStamp(g.licence_known_from) + '; for receipts received earlier it is not known.']
      : []),
  ]
}

export const COVERAGE_NONE_TITLE = 'No receipts are recorded outside coverage'

export interface StockDetail {
  stockSource: StockRow & { id: string }
  ledger: Ledger
  movements: StockMovement[]
  allocations: StockAllocation[]
  pallets: StockPallet[]
  receipts: StockReceipt[]
}

export type HoldFilter = '' | 'held' | 'clear'

export const HOLD_FILTERS: [HoldFilter, string][] = [
  ['', 'Any hold state'],
  ['held', 'Held'],
  ['clear', 'No hold'],
]

export interface StockFilters {
  q: string
  variety: string
  contractor: string
  hold: HoldFilter
}

// V4 srcLabel plus the job, the lid and the block: what the search box says
// it searches ("source, variety, job").
const searchText = (r: StockRow) =>
  [r.id, r.variety, r.box_type, r.job_name, r.site_name, r.block_name, r.contractor_name, r.lid_name, fmtDay(r.harvest_date)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

export function filterStock(rows: StockRow[], f: StockFilters): StockRow[] {
  const q = f.q.trim().toLowerCase()
  return rows.filter(
    (r) =>
      (!f.variety || r.variety === f.variety) &&
      (!f.contractor || (r.contractor_name || '') === f.contractor) &&
      (f.hold !== 'held' || r.boxes_held > 0) &&
      (f.hold !== 'clear' || r.boxes_held === 0) &&
      (!q || searchText(r).includes(q))
  )
}

export const distinct = (xs: (string | null | undefined)[]) =>
  [...new Set(xs.filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b))

const sum = (rows: StockRow[], f: (r: StockRow) => number) => rows.reduce((a, r) => a + f(r), 0)

// V4 stTotal, with "1 box" rather than V4's "1 boxes".
export const stockTotal = (rows: StockRow[]) =>
  plural(rows.length, 'source group') +
  ' · ' +
  plural(sum(rows, (r) => r.boxes_on_hand), 'box', 'boxes') +
  ' on hand · ' +
  sum(rows, (r) => r.boxes_available) +
  ' available'

// The summary view (Adrian & Vanessa, 2026-11-02): the source groups that
// differ only by harvest date, site or block, as one row of variety, pack
// (box type, capacity, lid at picking) and contractor. The figures are the
// sums of the API's; the dated rows stay under the summary row.
export interface StockSummary {
  key: string
  variety: string
  box_type: string
  capacity_per_pallet: number
  lid_name: string | null
  contractor_name: string | null
  rows: StockRow[]
  first_harvest: string | null
  last_harvest: string | null
  boxes_on_hand: number
  boxes_held: number
  boxes_reserved: number
  boxes_available: number
  boxes_palletised: number
  boxes_confirmed_on_pallets: number
  boxes_shortfall: number
}

export const summaryKey = (r: StockRow) =>
  [r.variety, r.box_type, r.capacity_per_pallet, r.lid_name || '', r.contractor_name || ''].join('|')

export function summariseStock(rows: StockRow[]): StockSummary[] {
  const out = new Map<string, StockSummary>()
  for (const r of rows) {
    const key = summaryKey(r)
    let s = out.get(key)
    if (!s) {
      s = {
        key,
        variety: r.variety,
        box_type: r.box_type,
        capacity_per_pallet: r.capacity_per_pallet,
        lid_name: r.lid_name || null,
        contractor_name: r.contractor_name || null,
        rows: [],
        first_harvest: null,
        last_harvest: null,
        boxes_on_hand: 0,
        boxes_held: 0,
        boxes_reserved: 0,
        boxes_available: 0,
        boxes_palletised: 0,
        boxes_confirmed_on_pallets: 0,
        boxes_shortfall: 0,
      }
      out.set(key, s)
    }
    s.rows.push(r)
    if (r.harvest_date && (!s.first_harvest || r.harvest_date < s.first_harvest)) s.first_harvest = r.harvest_date
    if (r.harvest_date && (!s.last_harvest || r.harvest_date > s.last_harvest)) s.last_harvest = r.harvest_date
    s.boxes_on_hand += r.boxes_on_hand
    s.boxes_held += r.boxes_held
    s.boxes_reserved += r.boxes_reserved
    s.boxes_available += r.boxes_available
    s.boxes_palletised += r.boxes_palletised ?? 0
    s.boxes_confirmed_on_pallets += r.boxes_confirmed_on_pallets ?? 0
    s.boxes_shortfall += r.boxes_shortfall ?? 0
  }
  return [...out.values()].sort(
    (a, b) =>
      a.variety.localeCompare(b.variety) ||
      a.box_type.localeCompare(b.box_type) ||
      (a.contractor_name || '').localeCompare(b.contractor_name || '') ||
      (a.lid_name || '').localeCompare(b.lid_name || '')
  )
}

export const harvestRange = (s: Pick<StockSummary, 'first_harvest' | 'last_harvest'>) =>
  !s.first_harvest ? '—' : s.first_harvest === s.last_harvest ? fmtDay(s.first_harvest) : fmtDay(s.first_harvest) + ' – ' + fmtDay(s.last_harvest)

export const summaryTotal = (groups: StockSummary[], rows: StockRow[]) =>
  plural(groups.length, 'line') +
  ' · ' +
  plural(sum(rows, (r) => r.boxes_on_hand), 'box', 'boxes') +
  ' on hand · ' +
  sum(rows, (r) => r.boxes_available) +
  ' available'

export const stockExportNote = (rows: StockRow[]) =>
  plural(rows.length, 'source group') + ' as shown · ' + plural(sum(rows, (r) => r.boxes_on_hand), 'box', 'boxes') + ' on hand'

export const siteText = (r: Pick<StockRow, 'site_name' | 'block_name'>) =>
  [r.site_name || '—', r.block_name].filter(Boolean).join(' · ')

export const rowShort = (r: Pick<StockRow, 'boxes_shortfall'>) => (r.boxes_shortfall ?? 0) + ' boxes short against reservations'

// What a deep link asked a view to open with (the Overview's tiles and rows,
// handoff §7n.1a). Read once, as the view mounts: it is where the screen
// starts, not state the address bar keeps in step with.
export function urlParam(key: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(key)
  } catch {
    return null
  }
}

// Once read, the link's keys leave the address bar, so switching a sub-tab
// (which remounts the view) does not open the same record again. Called from
// an effect, not the initialiser, as StrictMode runs initialisers twice.
export function dropUrlParams(keys: string[]) {
  try {
    const u = new URL(window.location.href)
    if (!keys.some((k) => u.searchParams.has(k))) return
    keys.forEach((k) => u.searchParams.delete(k))
    window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash)
  } catch {
    // no history to rewrite
  }
}

export function oneOf<T extends string>(v: string | null, allowed: readonly T[], fallback: T): T {
  return v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
}

export const palletisedText = (r: Pick<StockRow, 'boxes_palletised' | 'boxes_confirmed_on_pallets'>) =>
  (r.boxes_palletised ?? 0) + ' planned · ' + (r.boxes_confirmed_on_pallets ?? 0) + ' confirmed'

// The drawer header (V4 stName / stSub).
export const drawerTitle = (s: StockRow) => s.variety + ' · ' + s.box_type
export const drawerSub = (s: StockRow) =>
  [siteText(s), s.contractor_name || '—', 'harvested ' + fmtDay(s.harvest_date), 'job ' + (s.job_name || '—')].join(' · ')

// V4 stFacts, in its order.
export const ledgerFacts = (l: Ledger): [string, number][] => [
  ['On hand', l.on_hand],
  ['Held', l.held],
  ['Reserved', l.reserved],
  ['Available', l.available],
  ['Planned on pallets', l.palletised],
  ['Confirmed on pallets', l.confirmed_on_pallets],
  ['Departed', l.departed],
  ['Written off', l.written_off],
  ...(l.adjusted ? ([['Stock take adjustments', l.adjusted]] as [string, number][]) : []),
]

export const capacityNote = (s: StockRow) =>
  'Capacity ' + s.capacity_per_pallet + ' boxes per pallet, snapshotted from the picking job. Lid at picking: ' + (s.lid_name || 'not specified') + '.'

export const shortBanner = (l: Ledger) => l.shortfall + ' boxes are both held and reserved. Affected orders need shortage resolution.'

const MOVE_WORD: Record<string, string> = {
  hold: 'Held',
  release: 'Hold released ·',
  write_off: 'Written off',
  dispatch: 'Departed',
}

// A reservation's movement carries its own wording ("Reserved for ORD-1");
// the others read verb, boxes, reason.
function movementText(m: StockMovement): string {
  const boxes = plural(Math.abs(m.quantity), 'box', 'boxes')
  if (m.movement_type === 'stock_take')
    return (m.quantity > 0 ? 'Found in stock take ' : 'Missing in stock take ') + boxes + (m.reason ? ' · ' + m.reason : '')
  if ((m.movement_type === 'allocate' || m.movement_type === 'deallocate') && m.reason) return m.reason + ' · ' + boxes
  return (MOVE_WORD[m.movement_type] || m.movement_type) + ' ' + boxes + (m.reason ? ' · ' + m.reason : '')
}

export interface HistoryLine {
  key: string
  at: string
  when: string
  what: string
  by: string
}

// V4 stHist: receipts, reservations, holds, write-offs and departures, newest
// first. A receipt is written twice (a stock_receipts row and a 'receipt'
// movement, services/stock-producer.js); the receipt row is the one shown, as
// it says who scanned it.
export function historyLines(d: Pick<StockDetail, 'receipts' | 'movements'>): HistoryLine[] {
  const out: HistoryLine[] = [
    ...d.receipts.map((r) => ({
      key: 'r:' + r.id,
      at: r.at,
      when: fmtStamp(r.at),
      what: 'Receipt ' + (chipLabel(r.item_code) || r.cycle_id) + ' · ' + plural(r.boxes, 'box', 'boxes'),
      by: [r.by || 'Unknown', r.how].filter(Boolean).join(' · '),
    })),
    ...d.movements
      .filter((m) => m.movement_type !== 'receipt')
      .map((m) => ({
        key: 'm:' + m.id,
        at: m.created_at,
        when: fmtStamp(m.created_at),
        what: movementText(m),
        by: m.created_by || 'Unknown',
      })),
  ]
  return out.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
}

// The limit each change can take, as the API sets it (routes/stock-dispatch.js
// STOCK_CHANGES): hold what is on hand and not already held, release what is
// held, write off what is on hand.
export type ChangeKind = 'hold' | 'release' | 'write-off'

export const changeLimit = (kind: ChangeKind, l: Ledger) =>
  Math.max(0, kind === 'hold' ? l.on_hand - l.source_held : kind === 'release' ? l.source_held : l.on_hand)

// V4 hwTitle: the panel's title and its commit button.
export const CHANGE_TITLE: Record<ChangeKind, string> = { hold: 'Hold stock', release: 'Release hold', 'write-off': 'Write off stock' }

// V4 hwApply's toast, and ours for a release.
export const changeDone = (kind: ChangeKind, n: number, s: StockRow) =>
  (kind === 'hold' ? 'Held ' : kind === 'release' ? 'Released ' : 'Wrote off ') + plural(n, 'box', 'boxes') + ' of ' + drawerTitle(s)

// V4 srcLabel: which group a panel acts on.
export const sourceLabel = (s: StockRow) => [drawerTitle(s), siteText(s), fmtDay(s.harvest_date)].join(' · ')

export const changeMax = (kind: ChangeKind, l: Ledger) =>
  plural(changeLimit(kind, l), 'box', 'boxes') + (kind === 'hold' ? ' not yet held' : kind === 'release' ? ' on hold' : ' on hand')

// V4 hwHelp, word for word; release has no V4 panel (it released every hold
// at once), so its wording is ours.
export const CHANGE_HELP: Record<ChangeKind, string> = {
  hold:
    'A hold blocks boxes from being allocated or loaded. They stay on hand and keep their reservations. Nothing is removed and nothing is substituted. Release returns them to available. Where held boxes are already reserved, the affected orders show a shortage until you release the hold or re-allocate.',
  release:
    'Releasing returns held boxes to available. They were never removed, so nothing is added back to stock: on hand stays the same. Pallets from this group can depart again once no boxes are left on hold.',
  'write-off':
    'A write-off removes boxes from stock permanently. On hand drops, availability drops with it, and there is no undo. Use a hold if the boxes might come back. Reservations against them do not move automatically; the affected orders show a shortage for someone to resolve.',
}

export interface ChangePreview {
  kind: ChangeKind
  boxes: number
  limit: number
  error: string | null
  new_short: number
  lines: string[]
}

export interface OrderLineChoice {
  order_id: string
  order_reference: string
  customer_name: string | null
  approval_status: string
  approval_label: string
  line_id: string
  line_number: number
  unallocated: number
  suggested: number
}

// V4 allocateFromStock option label.
export const lineChoiceLabel = (l: OrderLineChoice) =>
  [l.order_reference, l.customer_name || '—', l.unallocated + ' short', l.approval_label].join(' · ')

// V4 allocateFromStock blocks, as you type; the API checks both again.
export function allocateErrors(qty: string, available: number, line: OrderLineChoice | undefined): string[] {
  const n = parseInt(qty, 10) || 0
  const out: string[] = []
  if (!line) return out
  if (n > available) out.push('Only ' + available + ' available.')
  if (n > line.unallocated) out.push('Line needs only ' + line.unallocated + '.')
  return out
}

// Stock take (187): a count of a stock line or one group, and what it would
// do (POST /api/stock/take/preview); the commit answers the same shape.
export type StockTakeScope = 'line' | 'source'
export interface StockTakeChange {
  source_id: string
  harvest_date: string | null
  site_name: string | null
  block_name: string | null
  before: number
  after: number
  delta: number
  new_short: number
  orders: string[]
}
export interface StockTakePreview {
  scope: StockTakeScope
  counted_pallets: number
  loose_boxes: number
  variety: string
  box_type: string
  capacity: number
  lid_name: string | null
  contractor_name: string | null
  groups: number
  system_boxes: number
  counted_boxes: number
  difference: number
  changes: StockTakeChange[]
  lines: string[]
}

export const differenceText = (n: number) =>
  n === 0 ? 'Matches' : n > 0 ? '+' + plural(n, 'box', 'boxes') + ' found' : plural(-n, 'box', 'boxes') + ' missing'
